import { useState, useEffect } from "react"
import { ArrowRight, Building2, MapPin, Users2, X, Link, Plus, Trash2, Save, AlertCircle } from "lucide-react"
import { FMOService } from "@/features/fmo"
import { OrganizationService } from "@/features/organizations"
import { IntakeLinkAssignmentService } from "@/features/fmo"
import { VendorIntakeForm } from "@/features/campaigns"
import type { Organization } from "@/features/organizations"
import { useCampaign } from "@/features/campaigns"

interface AssignLicensedAgentProps {
  campaignName?: string
}

interface IntakeLink {
  id: string
  alias: string
  lawFirmName: string
  intakeFormUrl: string
}

interface AssignmentTarget {
  id: string
  name: string
  type: 'organization' | 'state' | 'district' | 'team'
}

// New interfaces for simplified design
interface OrganizationManager {
  id: string
  name: string
  type: 'EM' | 'DM' | 'CM' | 'BM' | 'AM'
  email?: string
  phone?: string
}

interface IntakeLinkAssignment {
  id: string
  alias: string
  lawFirmName: string
  intakeFormUrl: string
  emAssignments: OrganizationManager[]
  dmAssignments: OrganizationManager[]
  cmAssignments: OrganizationManager[]
  bmAssignments: OrganizationManager[]
  amAssignments: OrganizationManager[]
}

export default function AssignLicensedAgent({ campaignName }: AssignLicensedAgentProps) {
  const { selectedCampaign } = useCampaign()
  
  // State for licensed agents with assignments
  const [intakeLinkAssignments, setIntakeLinkAssignments] = useState<IntakeLinkAssignment[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  // State for available managers (EM, DM, CM, BM, AM)
  const [availableManagers, setAvailableManagers] = useState<{
    EM: OrganizationManager[]
    DM: OrganizationManager[]
    CM: OrganizationManager[]
    BM: OrganizationManager[]
    AM: OrganizationManager[]
  }>({
    EM: [],
    DM: [],
    CM: [],
    BM: [],
    AM: []
  })
  const [managersLoading, setManagersLoading] = useState<boolean>(true)
  const [managersError, setManagersError] = useState<string | null>(null)

  // Save state
  const [saving, setSaving] = useState<boolean>(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false)

  // Fetch licensed agents and create assignments on component mount and when selectedCampaign changes
  useEffect(() => {
    const fetchIntakeLinksAndAssignments = async () => {
      // Don't fetch if no campaign is selected
      if (!selectedCampaign) {
        setIntakeLinkAssignments([])
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)
        
        // Fetch both licensed agents and existing assignments in parallel
        const [vendorIntakeForms, existingAssignments] = await Promise.all([
          FMOService.getVendorIntakeLinks(),
          IntakeLinkAssignmentService.getAssignments().catch(() => []) // Don't fail if no assignments exist
        ])
        
        // CRITICAL: Filter licensed agents by selectedCampaign.id
        const campaignFilteredIntakeForms = vendorIntakeForms.filter(
          (form: VendorIntakeForm) => form.campaignId === selectedCampaign.id
        )
        
        // Transform VendorIntakeForm data to IntakeLinkAssignment format
        const transformedLinks: IntakeLinkAssignment[] = campaignFilteredIntakeForms.map((form: VendorIntakeForm) => ({
          id: form.id,
          alias: form.alias || `${form.vendorName} - ${form.businessType}`, // Use alias field or fallback to generated alias
          lawFirmName: form.vendorName,
          intakeFormUrl: form.lawFirmLink || form.intakeUrl || form.email || "", // Use lawFirmLink field (Lawsuit Link)
          emAssignments: [],
          dmAssignments: [],
          cmAssignments: [],
          bmAssignments: [],
          amAssignments: []
        }))
        
        // Merge existing assignments with the fetched licensed agents
        if (existingAssignments && existingAssignments.length > 0) {
          const mergedLinks = transformedLinks.map(link => {
            const existingAssignment = existingAssignments.find(existing => existing.id === link.id)
            if (existingAssignment) {
              return {
                ...link,
                emAssignments: existingAssignment.emAssignments || [],
                dmAssignments: existingAssignment.dmAssignments || [],
                cmAssignments: existingAssignment.cmAssignments || [],
                bmAssignments: existingAssignment.bmAssignments || [],
                amAssignments: existingAssignment.amAssignments || []
              }
            }
            return link
          })
          setIntakeLinkAssignments(mergedLinks)
        } else {
          setIntakeLinkAssignments(transformedLinks)
        }
      } catch (err) {
        console.error('Failed to fetch licensed agents:', err)
        setError(err instanceof Error ? err.message : 'Failed to fetch licensed agents')
      } finally {
        setLoading(false)
      }
    }

    fetchIntakeLinksAndAssignments()
  }, [selectedCampaign])

  // Fetch available managers (EM, DM, CM, BM, AM) on component mount
  useEffect(() => {
    const fetchManagers = async () => {
      try {
        setManagersLoading(true)
        setManagersError(null)
        
        // Fetch real organizations by level from backend
        const [emOrgs, dmOrgs, cmOrgs, bmOrgs, amOrgs] = await Promise.all([
          OrganizationService.getOrganizationsByLevel('EM'),
          OrganizationService.getOrganizationsByLevel('DM'),
          OrganizationService.getOrganizationsByLevel('CM'),
          OrganizationService.getOrganizationsByLevel('BM'),
          OrganizationService.getOrganizationsByLevel('AM')
        ])
        
        // Transform Organization data to OrganizationManager format
        const managers = {
          EM: emOrgs.map(org => ({
            id: org.id,
            name: org.name,
            type: 'EM' as const
          })),
          DM: dmOrgs.map(org => ({
            id: org.id,
            name: org.name,
            type: 'DM' as const
          })),
          CM: cmOrgs.map(org => ({
            id: org.id,
            name: org.name,
            type: 'CM' as const
          })),
          BM: bmOrgs.map(org => ({
            id: org.id,
            name: org.name,
            type: 'BM' as const
          })),
          AM: amOrgs.map(org => ({
            id: org.id,
            name: org.name,
            type: 'AM' as const
          }))
        }
        
        setAvailableManagers(managers)
      } catch (err) {
        console.error('Failed to fetch managers:', err)
        setManagersError(err instanceof Error ? err.message : 'Failed to fetch managers')
      } finally {
        setManagersLoading(false)
      }
    }

    fetchManagers()
  }, [])

  // Assignment functions
  const addManagerToIntakeLink = (intakeLinkId: string, manager: OrganizationManager, managerType: 'EM' | 'DM' | 'CM' | 'BM' | 'AM') => {
    setIntakeLinkAssignments(prev => 
      prev.map(link => {
        if (link.id === intakeLinkId) {
          const currentAssignments = link[`${managerType.toLowerCase()}Assignments` as keyof IntakeLinkAssignment] as OrganizationManager[]
          
          // Check if manager is already assigned
          if (currentAssignments.some(m => m.id === manager.id)) {
            return link // No change if already assigned
          }
          
        return {
            ...link,
            [`${managerType.toLowerCase()}Assignments` as keyof IntakeLinkAssignment]: [...currentAssignments, manager]
          }
        }
        return link
      })
    )
  }

  const removeManagerFromIntakeLink = (intakeLinkId: string, managerId: string, managerType: 'EM' | 'DM' | 'CM' | 'BM' | 'AM') => {
    setIntakeLinkAssignments(prev => 
      prev.map(link => {
        if (link.id === intakeLinkId) {
          const currentAssignments = link[`${managerType.toLowerCase()}Assignments` as keyof IntakeLinkAssignment] as OrganizationManager[]
      
      return {
            ...link,
            [`${managerType.toLowerCase()}Assignments` as keyof IntakeLinkAssignment]: currentAssignments.filter(m => m.id !== managerId)
          }
        }
        return link
      })
    )
  }

  // Save assignments to backend
  const saveAssignments = async () => {
    try {
      setSaving(true)
      setSaveError(null)
      setSaveSuccess(false)
      
      await IntakeLinkAssignmentService.saveAssignments(intakeLinkAssignments)
      
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000) // Hide success message after 3 seconds
      
    } catch (err) {
      console.error('Failed to save assignments:', err)
      setSaveError(err instanceof Error ? err.message : 'Failed to save assignments')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Main Settings Container */}
      <div className="bg-white rounded-3xl shadow-xl border border-gray-200 overflow-hidden">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-red-50 to-pink-50 p-8 border-b border-gray-200">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg">
                <ArrowRight className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Assign Licensed Agent</h2>
                <p className="text-gray-600">Assign EM, DM, and CM managers to each licensed agent for {campaignName}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3 px-4 py-2 bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl">
                <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-semibold text-orange-800 whitespace-nowrap">Manager Assignment</span>
              </div>
              
              {/* Selected Campaign Display */}
              {selectedCampaign && (
                <div className="flex items-center gap-2 px-4 py-2 bg-primary-50 border border-primary-200 rounded-xl">
                  <span className="text-sm font-semibold text-primary-900 whitespace-nowrap">{selectedCampaign.name}</span>
                </div>
              )}
              
              {/* Save Button */}
              <button
                onClick={saveAssignments}
                disabled={saving}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm transition-all duration-200 whitespace-nowrap ${
                  saving
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : saveSuccess
                    ? 'bg-green-100 text-green-700 border border-green-200'
                    : 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl'
                }`}
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 bg-white/30 rounded animate-pulse"></div>
                    <span>Saving...</span>
                  </>
                ) : saveSuccess ? (
                  <>
                    <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                    <span>Saved!</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Save Assignments</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Save Error Display */}
        {saveError && (
          <div className="mx-8 mb-4 p-4 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-red-500 rounded-full"></div>
              <span className="text-red-700 font-semibold">Save Error</span>
            </div>
            <p className="text-red-600 text-sm mt-1">{saveError}</p>
            <button
              onClick={() => setSaveError(null)}
              className="text-red-500 hover:text-red-700 text-xs mt-2"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* No Campaign Selected Warning */}
        {!selectedCampaign && (
          <div className="mx-8 mb-6 p-4 bg-yellow-50 border-2 border-yellow-200 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-yellow-800">No Campaign Selected</h3>
                <p className="text-sm text-yellow-700 mt-1">
                  Please select a campaign from the dropdown above to view and manage licensed agent assignments.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Assignment Table */}
        <div className="p-8">
          {loading ? (
            // Shimmer loading state
            <div className="overflow-x-auto">
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden min-w-[800px]">
                {/* Table Header Shimmer */}
                <div className="bg-gray-50 border-b border-gray-200">
                  <div className="grid grid-cols-12 gap-4 p-4">
                    <div className="col-span-3">
                      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                    </div>
                    <div className="col-span-2">
                      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                    </div>
                    <div className="col-span-2">
                      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                    </div>
                    <div className="col-span-2">
                      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                    </div>
                    <div className="col-span-2">
                      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                    </div>
                    <div className="col-span-1">
                      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                    </div>
                  </div>
                </div>

                {/* Table Rows Shimmer */}
                <div className="divide-y divide-gray-200">
                  {[1, 2, 3, 4].map((index) => (
                    <div key={index} className="grid grid-cols-12 gap-4 p-4">
                      {/* Licensed Agent Info Shimmer */}
                      <div className="col-span-3">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 bg-gray-200 rounded-lg animate-pulse"></div>
                          <div className="flex-1 space-y-2">
                            <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4"></div>
                            <div className="h-3 bg-gray-200 rounded animate-pulse w-1/2"></div>
                            <div className="h-3 bg-gray-200 rounded animate-pulse w-2/3"></div>
                          </div>
                        </div>
                      </div>

                      {/* EM Assignments Shimmer */}
                      <div className="col-span-2">
                        <div className="space-y-2">
                          <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
                          <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
                        </div>
                      </div>
                      
                      {/* DM Assignments Shimmer */}
                      <div className="col-span-2">
                        <div className="space-y-2">
                          <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
                          <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
                        </div>
                      </div>

                      {/* CM Assignments Shimmer */}
                      <div className="col-span-2">
                        <div className="space-y-2">
                          <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
                          <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
                        </div>
                      </div>

                      {/* BM Assignments Shimmer */}
                      <div className="col-span-2">
                        <div className="space-y-2">
                          <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
                          <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
                        </div>
                      </div>
                      
                      {/* AM Assignments Shimmer */}
                      <div className="col-span-1">
                        <div className="space-y-2">
                          <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
                          <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : error ? (
            // Error state
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="text-red-600 mb-2">Failed to load licensed agents</div>
                <div className="text-sm text-gray-500">{error}</div>
                <button 
                  onClick={() => window.location.reload()} 
                  className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : intakeLinkAssignments.length === 0 ? (
            // Empty state
            <div className="flex items-center justify-center py-12">
              <div className="text-center text-gray-500">
                <div className="mb-2">No licensed agents available</div>
                <div className="text-sm">Create vendor licensed agents to get started</div>
              </div>
                  </div>
          ) : (
            // Licensed Agents Table with Horizontal Scroll
            <div className="overflow-x-auto">
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden min-w-[1400px]">
                {/* Table Header */}
                <div className="bg-gray-50 border-b border-gray-200">
                  <div className="grid grid-cols-[300px_repeat(5,1fr)] gap-4 p-4 text-sm font-semibold text-gray-700">
                    <div className="whitespace-nowrap">Licensed Agent</div>
                    <div className="text-center whitespace-nowrap">EM Assignments</div>
                    <div className="text-center whitespace-nowrap">DM Assignments</div>
                    <div className="text-center whitespace-nowrap">CM Assignments</div>
                    <div className="text-center whitespace-nowrap">BM Assignments</div>
                    <div className="text-center whitespace-nowrap">AM Assignments</div>
                  </div>
                </div>

                {/* Table Body */}
                <div className="divide-y divide-gray-200">
                  {intakeLinkAssignments.map((link) => (
                    <div key={link.id} className="grid grid-cols-[300px_repeat(5,1fr)] gap-4 p-4 hover:bg-gray-50 transition-colors">
                      
                      {/* Licensed Agent Info */}
                      <div>
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Link className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-gray-900 text-sm truncate" title={link.alias}>
                            {link.alias}
                          </div>
                          <div className="text-xs text-gray-500 truncate" title={link.lawFirmName}>
                            <span className="font-medium">Law Firm:</span> {link.lawFirmName}
                          </div>
                          <div className="text-xs text-blue-600 truncate mt-1" title={link.intakeFormUrl}>
                            <span className="font-medium">Lawsuit Link:</span> {link.intakeFormUrl}
                          </div>
                        </div>
                      </div>
                    </div>

                      {/* EM Assignments */}
                      <div>
                      <div className="space-y-2">
                        {link.emAssignments.map((manager) => (
                          <div key={manager.id} className="flex items-center justify-between bg-red-50 border border-red-200 rounded-lg px-2 py-1">
                            <span className="text-xs text-red-800 truncate" title={manager.name}>
                              {manager.name}
                            </span>
                                <button
                              onClick={() => removeManagerFromIntakeLink(link.id, manager.id, 'EM')}
                              className="text-red-500 hover:text-red-700 ml-1 flex-shrink-0"
                              title="Remove assignment"
                                >
                              <X className="w-3 h-3" />
                                </button>
                              </div>
                        ))}
                        {managersLoading ? (
                          <div className="w-full h-8 bg-gray-200 rounded animate-pulse"></div>
                        ) : (
                          <select
                            onChange={(e) => {
                              if (e.target.value) {
                                const manager = availableManagers.EM.find(m => m.id === e.target.value)
                                if (manager) {
                                  addManagerToIntakeLink(link.id, manager, 'EM')
                                  e.target.value = ''
                                }
                              }
                            }}
                            className="w-full text-xs border border-gray-300 rounded px-2 py-1 bg-white"
                            defaultValue=""
                          >
                            <option value="">+ Add EM</option>
                            {availableManagers.EM
                              .filter(manager => !link.emAssignments.some(assigned => assigned.id === manager.id))
                              .map((manager) => (
                                <option key={manager.id} value={manager.id}>
                                  {manager.name}
                                </option>
                              ))}
                          </select>
                        )}
                      </div>
                    </div>

                      {/* DM Assignments */}
                      <div>
                      <div className="space-y-2">
                        {link.dmAssignments.map((manager) => (
                          <div key={manager.id} className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-2 py-1">
                            <span className="text-xs text-green-800 truncate" title={manager.name}>
                              {manager.name}
                            </span>
                            <button
                              onClick={() => removeManagerFromIntakeLink(link.id, manager.id, 'DM')}
                              className="text-green-500 hover:text-green-700 ml-1 flex-shrink-0"
                              title="Remove assignment"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                        {managersLoading ? (
                          <div className="w-full h-8 bg-gray-200 rounded animate-pulse"></div>
                        ) : (
                          <select
                            onChange={(e) => {
                              if (e.target.value) {
                                const manager = availableManagers.DM.find(m => m.id === e.target.value)
                                if (manager) {
                                  addManagerToIntakeLink(link.id, manager, 'DM')
                                  e.target.value = ''
                                }
                              }
                            }}
                            className="w-full text-xs border border-gray-300 rounded px-2 py-1 bg-white"
                            defaultValue=""
                          >
                            <option value="">+ Add DM</option>
                            {availableManagers.DM
                              .filter(manager => !link.dmAssignments.some(assigned => assigned.id === manager.id))
                              .map((manager) => (
                                <option key={manager.id} value={manager.id}>
                                  {manager.name}
                                </option>
                              ))}
                          </select>
                        )}
                </div>
              </div>

                      {/* CM Assignments */}
                      <div>
                      <div className="space-y-2">
                        {link.cmAssignments.map((manager) => (
                          <div key={manager.id} className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-2 py-1">
                            <span className="text-xs text-blue-800 truncate" title={manager.name}>
                              {manager.name}
                            </span>
                            <button
                              onClick={() => removeManagerFromIntakeLink(link.id, manager.id, 'CM')}
                              className="text-blue-500 hover:text-blue-700 ml-1 flex-shrink-0"
                              title="Remove assignment"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                        {managersLoading ? (
                          <div className="w-full h-8 bg-gray-200 rounded animate-pulse"></div>
                        ) : (
                          <select
                            onChange={(e) => {
                              if (e.target.value) {
                                const manager = availableManagers.CM.find(m => m.id === e.target.value)
                                if (manager) {
                                  addManagerToIntakeLink(link.id, manager, 'CM')
                                  e.target.value = ''
                                }
                              }
                            }}
                            className="w-full text-xs border border-gray-300 rounded px-2 py-1 bg-white"
                            defaultValue=""
                          >
                            <option value="">+ Add CM</option>
                            {availableManagers.CM
                              .filter(manager => !link.cmAssignments.some(assigned => assigned.id === manager.id))
                              .map((manager) => (
                                <option key={manager.id} value={manager.id}>
                                  {manager.name}
                                </option>
                              ))}
                          </select>
                        )}
                  </div>
                </div>
                
                      {/* BM Assignments */}
                      <div>
                      <div className="space-y-2">
                        {link.bmAssignments.map((manager) => (
                          <div key={manager.id} className="flex items-center justify-between bg-purple-50 border border-purple-200 rounded-lg px-2 py-1">
                            <span className="text-xs text-purple-800 truncate" title={manager.name}>
                              {manager.name}
                            </span>
                            <button
                              onClick={() => removeManagerFromIntakeLink(link.id, manager.id, 'BM')}
                              className="text-purple-500 hover:text-purple-700 ml-1 flex-shrink-0"
                              title="Remove assignment"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                        {managersLoading ? (
                          <div className="w-full h-8 bg-gray-200 rounded animate-pulse"></div>
                        ) : (
                          <select
                            onChange={(e) => {
                              if (e.target.value) {
                                const manager = availableManagers.BM.find(m => m.id === e.target.value)
                                if (manager) {
                                  addManagerToIntakeLink(link.id, manager, 'BM')
                                  e.target.value = ''
                                }
                              }
                            }}
                            className="w-full text-xs border border-gray-300 rounded px-2 py-1 bg-white"
                            defaultValue=""
                          >
                            <option value="">+ Add BM</option>
                            {availableManagers.BM
                              .filter(manager => !link.bmAssignments.some(assigned => assigned.id === manager.id))
                              .map((manager) => (
                                <option key={manager.id} value={manager.id}>
                                  {manager.name}
                                </option>
                              ))}
                          </select>
                        )}
                      </div>
                    </div>

                      {/* AM Assignments */}
                      <div>
                      <div className="space-y-2">
                        {link.amAssignments.map((manager) => (
                          <div key={manager.id} className="flex items-center justify-between bg-orange-50 border border-orange-200 rounded-lg px-2 py-1">
                            <span className="text-xs text-orange-800 truncate" title={manager.name}>
                              {manager.name}
                            </span>
                                <button
                              onClick={() => removeManagerFromIntakeLink(link.id, manager.id, 'AM')}
                              className="text-orange-500 hover:text-orange-700 ml-1 flex-shrink-0"
                              title="Remove assignment"
                                >
                              <X className="w-3 h-3" />
                                </button>
                              </div>
                        ))}
                        {managersLoading ? (
                          <div className="w-full h-8 bg-gray-200 rounded animate-pulse"></div>
                        ) : (
                          <select
                            onChange={(e) => {
                              if (e.target.value) {
                                const manager = availableManagers.AM.find(m => m.id === e.target.value)
                                if (manager) {
                                  addManagerToIntakeLink(link.id, manager, 'AM')
                                  e.target.value = ''
                                }
                              }
                            }}
                            className="w-full text-xs border border-gray-300 rounded px-2 py-1 bg-white"
                            defaultValue=""
                          >
                            <option value="">+ Add AM</option>
                            {availableManagers.AM
                              .filter(manager => !link.amAssignments.some(assigned => assigned.id === manager.id))
                              .map((manager) => (
                                <option key={manager.id} value={manager.id}>
                                  {manager.name}
                                </option>
                              ))}
                          </select>
                        )}
                      </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
